import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tab,
  Tabs,
  Button,
  Avatar,
  Stack,
  Tooltip,
  IconButton,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { reportsAPI } from '../services/api';

interface Issue {
  field: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
}

interface CompanyQuality {
  name: string;
  registrationNumber?: string;
  issues: Issue[];
  qualityScore: number;
}

interface PersonQuality {
  name: string;
  types: string[];
  issues: Issue[];
  qualityScore: number;
}

interface OwnershipIssue {
  owner: string;
  owned: string;
  issue: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}

interface JurisdictionReport {
  jurisdiction: string;
  summary: {
    totalCompanies: number;
    totalPersons: number;
    totalOwnerships: number;
    companiesWithIssues: number;
    personsWithIssues: number;
    ownershipsWithIssues: number;
    overallQualityScore: number;
  };
  companies: CompanyQuality[];
  persons: PersonQuality[];
  ownershipIssues: OwnershipIssue[];
  missingData: {
    companiesMissingRegistrationNumber: string[];
    companiesMissingAddress: string[];
    companiesMissingIncorporationDate: string[];
    companiesMissingShareCapital: string[];
    companiesMissingDirectors: string[];
    personsMissingContactInfo: string[];
    personsMissingNationality: string[];
    ownershipsWithoutPercentage: Array<{ owner: string; owned: string }>;
  };
}

interface DataQualityReport {
  generatedAt: string;
  totalJurisdictions: number;
  executiveSummary: {
    totalCompanies: number;
    totalPersons: number;
    totalOwnerships: number;
    companiesWithIssues: number;
    personsWithIssues: number;
    ownershipsWithIssues: number;
    overallQualityScore: number;
  };
  jurisdictions: JurisdictionReport[];
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return 'error';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'info';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return <ErrorIcon fontSize="small" />;
    case 'HIGH':
      return <WarningIcon fontSize="small" />;
    case 'MEDIUM':
      return <InfoIcon fontSize="small" />;
    case 'LOW':
      return <InfoIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#4caf50';
  if (score >= 60) return '#ff9800';
  if (score >= 40) return '#ff5722';
  return '#f44336';
};

const DataQuality: React.FC = () => {
  const [report, setReport] = useState<DataQualityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportsAPI.getQualityReport();
      setReport(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load data quality report');
      console.error('Error fetching quality report:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Container>
    );
  }

  const topJurisdictions = [...report.jurisdictions]
    .sort((a, b) => b.summary.overallQualityScore - a.summary.overallQualityScore)
    .slice(0, 5);

  const bottomJurisdictions = [...report.jurisdictions]
    .sort((a, b) => a.summary.overallQualityScore - b.summary.overallQualityScore)
    .slice(0, 5);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Premium Header with Gradient */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #ed6c02 0%, #ff9800 100%)',
          borderRadius: 2,
          p: 3,
          mb: 3,
          color: 'white',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <AssessmentIcon sx={{ fontSize: 36 }} />
          </Avatar>
          <Box flex={1}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Data Quality Report
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Generated: {new Date(report.generatedAt).toLocaleString()}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh report">
              <IconButton onClick={fetchReport} sx={{ color: 'white' }} aria-label="Refresh report">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Back to Dashboard">
              <Button
                component={RouterLink}
                to="/"
                variant="outlined"
                startIcon={<HomeIcon />}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Dashboard
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* Executive Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Executive Summary
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%', bgcolor: 'grey.50' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 56, height: 56, bgcolor: getScoreColor(report.executiveSummary.overallQualityScore) }}>
                      <AssessmentIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box flex={1}>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Overall Quality Score
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" sx={{ color: getScoreColor(report.executiveSummary.overallQualityScore) }}>
                        {report.executiveSummary.overallQualityScore}
                        <Typography component="span" variant="h5" color="text.secondary">/100</Typography>
                      </Typography>
                    </Box>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={report.executiveSummary.overallQualityScore}
                    sx={{
                      mt: 2,
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getScoreColor(report.executiveSummary.overallQualityScore),
                        borderRadius: 5,
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                      <BusinessIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box flex={1}>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Companies
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">{report.executiveSummary.totalCompanies}</Typography>
                    </Box>
                  </Stack>
                  <Badge
                    badgeContent={report.executiveSummary.companiesWithIssues}
                    color="error"
                    max={999}
                    sx={{ mt: 2, width: '100%' }}
                  >
                    <Chip
                      size="small"
                      label={`${((report.executiveSummary.companiesWithIssues / report.executiveSummary.totalCompanies) * 100).toFixed(1)}% with issues`}
                      color="error"
                      sx={{ width: '100%' }}
                    />
                  </Badge>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'success.main' }}>
                      <PeopleIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box flex={1}>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Persons
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">{report.executiveSummary.totalPersons}</Typography>
                    </Box>
                  </Stack>
                  <Badge
                    badgeContent={report.executiveSummary.personsWithIssues}
                    color="error"
                    max={999}
                    sx={{ mt: 2, width: '100%' }}
                  >
                    <Chip
                      size="small"
                      label={`${((report.executiveSummary.personsWithIssues / report.executiveSummary.totalPersons) * 100).toFixed(1)}% with issues`}
                      color="error"
                      sx={{ width: '100%' }}
                    />
                  </Badge>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'warning.main' }}>
                      <CheckCircleIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box flex={1}>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Ownerships
                      </Typography>
                      <Typography variant="h3" fontWeight="bold">{report.executiveSummary.totalOwnerships}</Typography>
                    </Box>
                  </Stack>
                  <Badge
                    badgeContent={report.executiveSummary.ownershipsWithIssues}
                    color="warning"
                    max={999}
                    sx={{ mt: 2, width: '100%' }}
                  >
                    <Chip
                      size="small"
                      label={`${((report.executiveSummary.ownershipsWithIssues / report.executiveSummary.totalOwnerships) * 100).toFixed(1)}% with issues`}
                      color="warning"
                      sx={{ width: '100%' }}
                    />
                  </Badge>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                Top 5 Jurisdictions (Best Quality)
              </Typography>
            <List>
              {topJurisdictions.map((j, idx) => (
                <ListItem key={j.jurisdiction}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">
                          {idx + 1}. {j.jurisdiction}
                        </Typography>
                        <Chip
                          label={`${j.summary.overallQualityScore}/100`}
                          size="small"
                          sx={{ backgroundColor: getScoreColor(j.summary.overallQualityScore), color: 'white' }}
                        />
                      </Box>
                    }
                    secondary={`${j.summary.totalCompanies} companies, ${j.summary.totalPersons} persons`}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="error" />
                Bottom 5 Jurisdictions (Need Attention)
              </Typography>
            <List>
              {bottomJurisdictions.map((j, idx) => (
                <ListItem key={j.jurisdiction}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">
                          {idx + 1}. {j.jurisdiction}
                        </Typography>
                        <Chip
                          label={`${j.summary.overallQualityScore}/100`}
                          size="small"
                          color="error"
                          sx={{ backgroundColor: getScoreColor(j.summary.overallQualityScore), color: 'white' }}
                        />
                      </Box>
                    }
                    secondary={`${j.summary.companiesWithIssues}/${j.summary.totalCompanies} companies with issues`}
                  />
                </ListItem>
              ))}
            </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Jurisdictions Detail */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Jurisdiction Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click on each jurisdiction to view detailed data quality issues
        </Typography>

        {report.jurisdictions.map((jurisdiction) => (
          <Accordion key={jurisdiction.jurisdiction} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {jurisdiction.jurisdiction}
                </Typography>
                <Chip
                  label={`Score: ${jurisdiction.summary.overallQualityScore}/100`}
                  sx={{ backgroundColor: getScoreColor(jurisdiction.summary.overallQualityScore), color: 'white' }}
                />
                <Typography variant="body2" color="text.secondary">
                  {jurisdiction.summary.totalCompanies} companies • {jurisdiction.summary.totalPersons} persons
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label="Summary" />
                <Tab label="Companies" />
                <Tab label="Persons" />
                <Tab label="Missing Data" />
              </Tabs>

              {/* Summary Tab */}
              {selectedTab === 0 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            Companies with Issues
                          </Typography>
                          <Typography variant="h5">
                            {jurisdiction.summary.companiesWithIssues} / {jurisdiction.summary.totalCompanies}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            Persons with Issues
                          </Typography>
                          <Typography variant="h5">
                            {jurisdiction.summary.personsWithIssues} / {jurisdiction.summary.totalPersons}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="text.secondary" gutterBottom>
                            Ownerships with Issues
                          </Typography>
                          <Typography variant="h5">
                            {jurisdiction.summary.ownershipsWithIssues} / {jurisdiction.summary.totalOwnerships}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Companies Tab */}
              {selectedTab === 1 && (
                <Box>
                  {jurisdiction.companies.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      All companies have complete data!
                    </Alert>
                  ) : (
                    <List>
                      {jurisdiction.companies.slice(0, 10).map((company) => (
                        <React.Fragment key={company.name}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {company.name}
                                  </Typography>
                                  {company.registrationNumber && (
                                    <Chip label={company.registrationNumber} size="small" variant="outlined" />
                                  )}
                                  <Chip
                                    label={`Score: ${company.qualityScore}/100`}
                                    size="small"
                                    sx={{ backgroundColor: getScoreColor(company.qualityScore), color: 'white' }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {company.issues.map((issue, idx) => (
                                    <Box key={idx} display="flex" alignItems="flex-start" gap={1} mb={0.5}>
                                      <Chip
                                        icon={getSeverityIcon(issue.severity)}
                                        label={issue.severity}
                                        size="small"
                                        color={getSeverityColor(issue.severity) as any}
                                      />
                                      <Typography variant="body2" color="text.secondary">
                                        <strong>{issue.field}:</strong> {issue.description}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                      {jurisdiction.companies.length > 10 && (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                ... and {jurisdiction.companies.length - 10} more companies with issues
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  )}
                </Box>
              )}

              {/* Persons Tab */}
              {selectedTab === 2 && (
                <Box>
                  {jurisdiction.persons.length === 0 ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      All persons have complete data!
                    </Alert>
                  ) : (
                    <List>
                      {jurisdiction.persons.slice(0, 10).map((person) => (
                        <React.Fragment key={person.name}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {person.name}
                                  </Typography>
                                  {person.types.map((type) => (
                                    <Chip key={type} label={type} size="small" variant="outlined" />
                                  ))}
                                  <Chip
                                    label={`Score: ${person.qualityScore}/100`}
                                    size="small"
                                    sx={{ backgroundColor: getScoreColor(person.qualityScore), color: 'white' }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {person.issues.map((issue, idx) => (
                                    <Box key={idx} display="flex" alignItems="flex-start" gap={1} mb={0.5}>
                                      <Chip
                                        icon={getSeverityIcon(issue.severity)}
                                        label={issue.severity}
                                        size="small"
                                        color={getSeverityColor(issue.severity) as any}
                                      />
                                      <Typography variant="body2" color="text.secondary">
                                        <strong>{issue.field}:</strong> {issue.description}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                      {jurisdiction.persons.length > 10 && (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                ... and {jurisdiction.persons.length - 10} more persons with issues
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  )}
                </Box>
              )}

              {/* Missing Data Tab */}
              {selectedTab === 3 && (
                <Box>
                  <Grid container spacing={2}>
                    {jurisdiction.missingData.companiesMissingRegistrationNumber.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" color="error" gutterBottom>
                              Missing Registration Number ({jurisdiction.missingData.companiesMissingRegistrationNumber.length})
                            </Typography>
                            <List dense>
                              {jurisdiction.missingData.companiesMissingRegistrationNumber.slice(0, 5).map((name) => (
                                <ListItem key={name}>
                                  <Typography variant="body2">• {name}</Typography>
                                </ListItem>
                              ))}
                              {jurisdiction.missingData.companiesMissingRegistrationNumber.length > 5 && (
                                <ListItem>
                                  <Typography variant="body2" color="text.secondary">
                                    ... and {jurisdiction.missingData.companiesMissingRegistrationNumber.length - 5} more
                                  </Typography>
                                </ListItem>
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {jurisdiction.missingData.companiesMissingAddress.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                              Missing Address ({jurisdiction.missingData.companiesMissingAddress.length})
                            </Typography>
                            <List dense>
                              {jurisdiction.missingData.companiesMissingAddress.slice(0, 5).map((name) => (
                                <ListItem key={name}>
                                  <Typography variant="body2">• {name}</Typography>
                                </ListItem>
                              ))}
                              {jurisdiction.missingData.companiesMissingAddress.length > 5 && (
                                <ListItem>
                                  <Typography variant="body2" color="text.secondary">
                                    ... and {jurisdiction.missingData.companiesMissingAddress.length - 5} more
                                  </Typography>
                                </ListItem>
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    {jurisdiction.missingData.personsMissingNationality.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                              Persons Missing Nationality ({jurisdiction.missingData.personsMissingNationality.length})
                            </Typography>
                            <List dense>
                              {jurisdiction.missingData.personsMissingNationality.slice(0, 5).map((name) => (
                                <ListItem key={name}>
                                  <Typography variant="body2">• {name}</Typography>
                                </ListItem>
                              ))}
                              {jurisdiction.missingData.personsMissingNationality.length > 5 && (
                                <ListItem>
                                  <Typography variant="body2" color="text.secondary">
                                    ... and {jurisdiction.missingData.personsMissingNationality.length - 5} more
                                  </Typography>
                                </ListItem>
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Container>
  );
};

export default DataQuality;
